import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Divider,
  TextField,
  MenuItem,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  ImageList,
  ImageListItem
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useNavigate } from 'react-router-dom';
import { AdminOperationTreeDto, TreeOperationsQueryParams, AiEstimatedTimeDto } from './dtos/admin-operation-tree.dto';
import { getTreeOperations, extractFeaturesFromTreeOperation, getMostSimilarOperations, getAiEstimatedTime } from './admin.service';
import { logError } from '../../shared/services/logger.service';
import { enqueueSnackbar } from 'notistack';

import {formatAddressLine} from "../../shared/misc/location-helpers";
const TreeOperationsTab: React.FC = () => {
  const navigate = useNavigate();
  const [treeOperations, setTreeOperations] = useState<AdminOperationTreeDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Filter state
  const [operationType, setOperationType] = useState<string>('');
  const [availableOperationTypes, setAvailableOperationTypes] = useState<string[]>([]);

  // Most similar operations state
  const [mostSimilarMode, setMostSimilarMode] = useState(false);
  const [selectedOperationId, setSelectedOperationId] = useState<number | null>(null);
  const [originalOperations, setOriginalOperations] = useState<AdminOperationTreeDto[]>([]);

  // AI time estimates state
  const [aiEstimates, setAiEstimates] = useState<Record<number, AiEstimatedTimeDto>>({});
  const [loadingEstimates, setLoadingEstimates] = useState<Record<number, boolean>>({});

  // Helper function to format JSON details
  const formatJsonDetails = (details: string) => {
    if (!details) return 'No details available';
    
    try {
      const parsed = JSON.parse(details);
      return JSON.stringify(parsed, null, 2);
    } catch (error) {
      // If it's not valid JSON, return as-is
      return details;
    }
  };

  const fetchTreeOperations = async (params?: TreeOperationsQueryParams) => {
    setLoading(true);
    setError(null);
    try {
      const queryParams = {
        pageIndex,
        pageSize,
        ...(operationType && { operationType }),
        ...params
      };
      
      const response = await getTreeOperations(navigate, queryParams);
      setTreeOperations(response.data);
      setTotalCount(response.totalCount);
      setTotalPages(response.totalPages);
      setPageIndex(response.pageIndex);
      setPageSize(response.pageSize);
      
      // Extract unique operation types for the filter dropdown
      const uniqueTypes = Array.from(new Set(response.data.map(op => op.operationType)));
      setAvailableOperationTypes(prev => {
        const combined = Array.from(new Set([...prev, ...uniqueTypes]));
        return combined.sort();
      });
      
      enqueueSnackbar(`Loaded ${response.data.length} of ${response.totalCount} tree operations`, { variant: 'success' });
    } catch (err) {
      const errorMessage = 'Failed to load tree operations';
      setError(errorMessage);
      logError('Error fetching tree operations', {}, err as Error);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTreeOperations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, pageSize, operationType]);

  const formatCurrency = (amount?: number) => {
    return amount ? `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : 'N/A';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatHours = (hours?: number) => {
    if (!hours) return 'Not specified';
    return `${hours.toLocaleString('en-US', { 
      minimumFractionDigits: 1, 
      maximumFractionDigits: 2 
    })} hrs`;
  };

  const formatDistance = (distance?: number) => {
    if (distance === undefined || distance === null) return 'Not calculated';
    return distance.toLocaleString('en-US', { 
      minimumFractionDigits: 4, 
      maximumFractionDigits: 6 
    });
  };

  // The estimate's record page, which shows its job and invoice too; /invoices/ takes an invoice id, not this one (SQ-715).
  const handleViewRecord = (estimateId: number) => {
    window.open(`/estimates/${estimateId}`, '_blank');
  };

  const handleExtractFeatures = async (operationId: number) => {
    try {
      await extractFeaturesFromTreeOperation(navigate, operationId);
      enqueueSnackbar('Features extracted successfully', { variant: 'success' });
    } catch (err) {
      const errorMessage = 'Failed to extract features';
      logError('Error extracting features', { operationId }, err as Error);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleGetTimeEstimate = async (operationId: number) => {
    try {
      setLoadingEstimates(prev => ({ ...prev, [operationId]: true }));
      const estimate = await getAiEstimatedTime(navigate, operationId);
      setAiEstimates(prev => ({ ...prev, [operationId]: estimate }));
      enqueueSnackbar('Time estimate retrieved successfully', { variant: 'success' });
    } catch (err) {
      const errorMessage = 'Failed to get time estimate';
      logError('Error getting time estimate', { operationId }, err as Error);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoadingEstimates(prev => ({ ...prev, [operationId]: false }));
    }
  };

  const handleShowMostSimilar = async (operationId: number) => {
    try {
      setLoading(true);
      // Store original operations so we can return to them
      setOriginalOperations(treeOperations);
      
      // Get most similar operations
      const similarOperations = await getMostSimilarOperations(navigate, operationId);
      
      // Find the selected operation
      const selectedOperation = treeOperations.find(op => op.id === operationId);
      
      if (selectedOperation) {
        // Show selected operation first, then similar operations
        setTreeOperations([selectedOperation, ...similarOperations]);
        setSelectedOperationId(operationId);
        setMostSimilarMode(true);
        enqueueSnackbar(`Found ${similarOperations.length} similar operations`, { variant: 'success' });
      }
    } catch (err) {
      const errorMessage = 'Failed to load similar operations';
      logError('Error fetching similar operations', { operationId }, err as Error);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleReturnToAll = () => {
    setTreeOperations(originalOperations);
    setMostSimilarMode(false);
    setSelectedOperationId(null);
    setOriginalOperations([]);
    enqueueSnackbar('Returned to all operations', { variant: 'info' });
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPageIndex(value - 1); // Material-UI Pagination is 1-based, our API is 0-based
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPageSize(parseInt(event.target.value));
    setPageIndex(0); // Reset to first page when changing page size
  };

  const handleOperationTypeChange = (event: SelectChangeEvent<string>) => {
    setOperationType(event.target.value);
    setPageIndex(0); // Reset to first page when filtering
  };

  const handleRefresh = () => {
    // Reset most similar mode when refreshing
    setMostSimilarMode(false);
    setSelectedOperationId(null);
    setOriginalOperations([]);
    fetchTreeOperations();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box p={2}>
      {/* Header with title and controls */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
        <Typography variant="h5">
          {mostSimilarMode ? 'Most Similar Operations' : `Tree Operations (${totalCount} total)`}
        </Typography>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh}>
          Refresh
        </Button>
      </Box>

      {/* Filters and pagination controls */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap" alignItems="center">
        <FormControl size="small" sx={{ minWidth: 200 }} disabled={mostSimilarMode}>
          <InputLabel>Operation Type</InputLabel>
          <Select
            value={operationType}
            label="Operation Type"
            onChange={handleOperationTypeChange}
            disabled={mostSimilarMode}
          >
            <MenuItem value="">All Types</MenuItem>
            {availableOperationTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          select
          size="small"
          label="Page Size"
          value={pageSize}
          onChange={handlePageSizeChange}
          sx={{ minWidth: 100 }}
          disabled={mostSimilarMode}
        >
          <MenuItem value={5}>5</MenuItem>
          <MenuItem value={10}>10</MenuItem>
          <MenuItem value={25}>25</MenuItem>
          <MenuItem value={50}>50</MenuItem>
        </TextField>

        <Typography variant="body2" color="text.secondary">
          {mostSimilarMode 
            ? `Showing ${treeOperations.length} similar operations` 
            : `Showing ${treeOperations.length} of ${totalCount} results`
          }
        </Typography>
      </Box>

      {/* Results */}
      {treeOperations.length === 0 ? (
        <Alert severity="info">No tree operations found.</Alert>
      ) : (
        <Box>
          {treeOperations.map((operation) => (
            <Card 
              key={operation.id} 
              sx={{ 
                mb: 2,
                ...(mostSimilarMode && operation.id === selectedOperationId && {
                  border: 2,
                  borderColor: 'primary.main',
                  backgroundColor: 'primary.50'
                })
              }}
            >
              <CardContent>
                {mostSimilarMode && operation.id === selectedOperationId && (
                  <Box mb={2}>
                    <Chip 
                      label="Selected Operation" 
                      color="primary" 
                      size="small"
                      sx={{ mb: 1 }}
                    />
                  </Box>
                )}
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="h6" color="primary">
                      {operation.operationType}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ID: {operation.id}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2">
                      <strong>Estimated Cost:</strong> {formatCurrency(operation.estimatedCost)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Estimated Time:</strong> {formatHours(operation.estimatedTimeHours)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Actual Time:</strong> {formatHours(operation.actualTimeHours)}
                    </Typography>
                    {aiEstimates[operation.id] && (
                      <Box sx={{ mt: 1, p: 1, bgcolor: 'primary.50', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                          AI Time Estimates:
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                          Low: {formatHours(aiEstimates[operation.id].lowTimeHours)}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                          Medium: {formatHours(aiEstimates[operation.id].mediumTimeHours)}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                          High: {formatHours(aiEstimates[operation.id].highTimeHours)}
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2">
                      <strong>Total Job Cost:</strong> {formatCurrency(operation.estimateTotalCost)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Total Job Time:</strong> {operation.estimateTotalJobTimeMinutes ? 
                        formatHours(operation.estimateTotalJobTimeMinutes / 60) : 'Not specified'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Embedding Distance:</strong> {formatDistance(operation.embeddingDistance)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2">
                      <strong>Details:</strong>
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        mt: 1,
                        p: 1,
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        fontFamily: 'monospace',
                        overflow: 'auto',
                        maxHeight: '150px',
                        maxWidth: '100%',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}
                    >
                      {formatJsonDetails(operation.details)}
                    </Box>
                  </Grid>
                </Grid>

                {/* Action Buttons */}
                <Box display="flex" gap={1} mt={2} flexWrap="wrap">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ReceiptIcon />}
                    onClick={() => handleViewRecord(operation.estimateId)}
                  >
                    Open Record
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AutoAwesomeIcon />}
                    onClick={() => handleExtractFeatures(operation.id)}
                  >
                    Extract Features
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AccessTimeIcon />}
                    onClick={() => handleGetTimeEstimate(operation.id)}
                    disabled={loadingEstimates[operation.id]}
                  >
                    {loadingEstimates[operation.id] ? 'Getting Estimate...' : 'Get Time Estimate'}
                  </Button>
                  {mostSimilarMode && operation.id === selectedOperationId ? (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<ArrowBackIcon />}
                      onClick={handleReturnToAll}
                    >
                      Return to All
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<FindInPageIcon />}
                      onClick={() => handleShowMostSimilar(operation.id)}
                      disabled={mostSimilarMode}
                    >
                      Show Most Similar
                    </Button>
                  )}
                </Box>

                <Accordion sx={{ mt: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1">
                      Operation Details
                      {operation.treeImages.length > 0 && (
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                          ({operation.treeImages.length} images)
                        </Typography>
                      )}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>Operation Info</Typography>
                        {operation.operationNotes && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            <strong>Notes:</strong> {operation.operationNotes}
                          </Typography>
                        )}
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Client:</strong> {operation.clientName}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Status:</strong> {operation.estimateStatus}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Created:</strong> {formatDate(operation.createdAt)}
                        </Typography>
                        {operation.dragPathDistanceFeet && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            <strong>Drag Path:</strong> {operation.dragPathDistanceFeet} ft
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>Tree Information</Typography>
                        {operation.treeSpecies && (
                          <Typography variant="body2"><strong>Species:</strong> {operation.treeSpecies}</Typography>
                        )}
                        {operation.treeHeight && (
                          <Typography variant="body2"><strong>Height:</strong> {operation.treeHeight}</Typography>
                        )}
                        {operation.treeWidth && (
                          <Typography variant="body2"><strong>Width:</strong> {operation.treeWidth}</Typography>
                        )}
                        {operation.treeTrunkDiameter && (
                          <Typography variant="body2"><strong>Trunk Diameter:</strong> {operation.treeTrunkDiameter}</Typography>
                        )}
                        {operation.treeNotes && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            <strong>Tree Notes:</strong> {operation.treeNotes}
                          </Typography>
                        )}
                        {operation.treeCrewNotes && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            <strong>Crew Notes:</strong> {operation.treeCrewNotes}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>Estimate Details</Typography>
                        <Typography variant="body2"><strong>Name:</strong> {operation.estimateName}</Typography>
                        <Typography variant="body2"><strong>Full Address:</strong> {formatAddressLine(operation.estimateAddress, operation.estimateCity, operation.estimateState, operation.estimatePostalCode)}</Typography>
                        <Typography variant="body2"><strong>City/State/ZIP:</strong> {operation.estimateCity}, {operation.estimateState} {operation.estimatePostalCode}</Typography>
                        <Typography variant="body2"><strong>Total Trees:</strong> {operation.estimateCountTrees}</Typography>
                        {operation.crewSize && (
                          <Typography variant="body2"><strong>Crew Size:</strong> {operation.crewSize}</Typography>
                        )}
                        {operation.estimateNotes && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            <strong>Estimate Notes:</strong> {operation.estimateNotes}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>Additional Info</Typography>
                        <Typography variant="body2"><strong>Tree ID:</strong> {operation.treeId}</Typography>
                        <Typography variant="body2"><strong>Estimate ID:</strong> {operation.estimateId}</Typography>
                        <Typography variant="body2"><strong>Client ID:</strong> {operation.clientId}</Typography>
                        <Typography variant="body2"><strong>Created By:</strong> {operation.createdBy}</Typography>
                        <Typography variant="body2"><strong>Updated:</strong> {formatDate(operation.updatedAt)}</Typography>
                        
                        {operation.treeCustomOperationMethods.length > 0 && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                              Custom Methods ({operation.treeCustomOperationMethods.length}):
                            </Typography>
                            {operation.treeCustomOperationMethods.map((method, index) => (
                              <Typography key={method.id} variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                                {method.name}
                                {method.hourlyRate && ` - ${formatCurrency(method.hourlyRate)}/hr`}
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </Grid>
                    </Grid>

                    {/* Images Section */}
                    {operation.treeImages.length > 0 && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" gutterBottom>Tree Images</Typography>
                        <ImageList sx={{ width: '100%', height: 'auto' }} cols={3} rowHeight={150}>
                          {operation.treeImages.map((image) => (
                            <ImageListItem key={image.id}>
                              <img
                                src={image.imageUrl}
                                alt={`Tree ${image.id}`}
                                loading="lazy"
                                style={{
                                  width: '100%',
                                  height: '150px',
                                  objectFit: 'contain',
                                  cursor: 'pointer',
                                  borderRadius: '4px'
                                }}
                                onClick={() => window.open(image.imageUrl, '_blank')}
                              />
                              <Box 
                                sx={{ 
                                  position: 'absolute', 
                                  bottom: 0, 
                                  left: 0, 
                                  right: 0, 
                                  background: 'rgba(0,0,0,0.7)', 
                                  color: 'white', 
                                  p: 0.5,
                                  borderBottomLeftRadius: '4px',
                                  borderBottomRightRadius: '4px'
                                }}
                              >
                                <Typography variant="caption" display="block">
                                  {formatDate(image.createdAt)}
                                </Typography>
                              </Box>
                            </ImageListItem>
                          ))}
                        </ImageList>
                      </>
                    )}
                  </AccordionDetails>
                </Accordion>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Pagination */}
      {totalPages > 1 && !mostSimilarMode && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={pageIndex + 1} // Material-UI is 1-based, our API is 0-based
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Box>
  );
};

export default TreeOperationsTab; 